import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  AlertTriangle, Flame, Car, Activity, AlertCircle, HelpCircle, 
  ChevronRight, Radio, ZoomIn, Layers, Map as MapIcon,
  CloudLightning, ShieldAlert, UserX, Camera, Headphones, Users, Play, Signal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// USA center coordinates
const USA_CENTER = [39.8283, -98.5795];
const USA_ZOOM = 4;

// Map tile options
const MAP_TILES = {
  dark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>'
  },
  light: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>'
  }
};

const INCIDENT_ICONS = {
  crime: AlertTriangle,
  fire: Flame,
  traffic: Car,
  medical: Activity,
  hazard: AlertCircle,
  other: HelpCircle,
  weather: CloudLightning,
  ice_activity: ShieldAlert,
  sex_offender: UserX,
  security_camera: Camera
};

const SEVERITY_COLORS = {
  critical: '#ef4444',
  major: '#f97316',
  minor: '#eab308',
  info: '#3b82f6'
};

const TYPE_COLORS = {
  crime: '#ef4444',
  fire: '#f97316',
  traffic: '#eab308',
  medical: '#3b82f6',
  hazard: '#a855f7',
  other: '#6b7280',
  weather: '#06b6d4',
  ice_activity: '#f97316',
  sex_offender: '#dc2626',
  security_camera: '#6b7280'
};

// SVG path data for each icon type
const ICON_PATHS = {
  crime: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  fire: '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
  traffic: '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>',
  medical: '<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>',
  hazard: '<circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/>',
  other: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  weather: '<path d="M6 16.326A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 .5 8.973"/><path d="m13 12-3 5h4l-3 5"/>',
  ice_activity: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/>',
  sex_offender: '<circle cx="12" cy="5" r="3"/><path d="M12 22V8"/><path d="m5 12 7 3 7-3"/>',
  security_camera: '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>'
};

// Create custom icon for incidents
const createIncidentIcon = (type, severity, isActive) => {
  const color = TYPE_COLORS[type] || '#6b7280';
  const iconPath = ICON_PATHS[type] || ICON_PATHS.other;
  
  const iconHtml = `
    <div style="
      position: relative;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      ${isActive ? `
        <div style="
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: ${color};
          opacity: 0.35;
        "></div>
      ` : ''}
      <div style="
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: ${color};
        border: 3px solid ${isActive ? '#fff' : 'rgba(255,255,255,0.8)'};
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px ${color}80, 0 0 20px ${color}40;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          ${iconPath}
        </svg>
      </div>
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-incident-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20]
  });
};

// Create cluster icon
const createClusterIcon = (count, hasCritical, isDarkTheme) => {
  const color = hasCritical ? '#ef4444' : '#14b8a6';
  const size = Math.min(56, Math.max(32, 28 + Math.log(count) * 8));
  const borderColor = isDarkTheme ? 'rgba(255,255,255,0.9)' : 'white';
  
  const iconHtml = `
    <div style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      ${hasCritical ? `
        <div style="
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: ${color};
          opacity: 0.35;
        "></div>
      ` : ''}
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: linear-gradient(135deg, ${color} 0%, ${color}dd 100%);
        border: 3px solid ${borderColor};
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 15px ${color}60, 0 0 30px ${color}30;
        font-weight: bold;
        color: white;
        font-size: ${size > 40 ? '14px' : '12px'};
        font-family: ui-monospace, monospace;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      ">
        ${count}
      </div>
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-cluster-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  });
};

// Create scanner feed marker icon
const createScannerIcon = (feedCount, isOnline, isDarkTheme) => {
  const color = '#14b8a6'; // Teal for scanner
  const size = 40;
  const borderColor = isDarkTheme ? 'rgba(255,255,255,0.9)' : 'white';
  
  const iconHtml = `
    <div style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    ">
      ${isOnline ? `
        <div style="
          position: absolute;
          inset: -2px;
          border-radius: 50%;
          background: ${color};
          opacity: 0.4;
          pointer-events: none;
        "></div>
      ` : ''}
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: linear-gradient(135deg, ${color} 0%, #0d9488 100%);
        border: 3px solid ${borderColor};
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 15px ${color}60, 0 0 20px ${color}40;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        pointer-events: none;
      ">
        <svg style="pointer-events: none;" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
        </svg>
      </div>
      ${feedCount > 1 ? `
        <div style="
          position: absolute;
          top: -4px;
          right: -4px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #ef4444;
          border: 2px solid ${borderColor};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          color: white;
          pointer-events: none;
        ">${feedCount}</div>
      ` : ''}
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-scanner-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -20]
  });
};

// Map controller component for flying to locations
const MapController = ({ center, zoom, focusLocation }) => {
  const map = useMap();
  
  useEffect(() => {
    // Handle focus location changes - flies to location when scanner is played
    if (focusLocation && focusLocation.lat && focusLocation.lng) {
      map.flyTo([focusLocation.lat, focusLocation.lng], focusLocation.zoom || 11, { duration: 1.5 });
    }
  }, [focusLocation, map]);
  
  return null;
};

// Heatmap layer using circle markers
const HeatmapLayer = ({ incidents }) => {
  return incidents.map((incident, index) => {
    const color = TYPE_COLORS[incident.type] || '#6b7280';
    const radius = incident.severity === 'critical' ? 25 : incident.severity === 'major' ? 20 : 15;
    
    return (
      <CircleMarker
        key={`heat-${incident.id}-${index}`}
        center={[incident.location.lat, incident.location.lng]}
        radius={radius}
        pathOptions={{
          color: 'transparent',
          fillColor: color,
          fillOpacity: 0.3,
          weight: 0
        }}
      />
    );
  });
};

// Popup content component
const IncidentPopupContent = ({ incident }) => {
  const timeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const color = TYPE_COLORS[incident.type] || '#6b7280';
  
  // Type-specific labels
  const getTypeLabel = (type) => {
    const labels = {
      crime: 'Criminal Activity',
      fire: 'Fire/Rescue',
      traffic: 'Traffic Incident',
      medical: 'Medical Emergency',
      hazard: 'Public Hazard',
      weather: 'Weather Alert',
      ice_activity: 'ICE Activity',
      sex_offender: 'Sex Offender',
      security_camera: 'Security Camera',
      other: 'Other'
    };
    return labels[type] || 'Incident';
  };
  
  return (
    <div className="p-3 min-w-[280px]" data-testid="incident-popup">
      <div className="flex items-start gap-3 mb-2">
        <div 
          className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          {React.createElement(INCIDENT_ICONS[incident.type] || HelpCircle, {
            size: 18,
            style: { color }
          })}
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-800 leading-tight">
            {incident.title || incident.name || incident.event_type || getTypeLabel(incident.type)}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {incident.severity && (
              <span 
                className="px-1.5 py-0.5 rounded text-white uppercase font-bold text-[10px]"
                style={{ backgroundColor: SEVERITY_COLORS[incident.severity] || '#6b7280' }}
              >
                {incident.severity}
              </span>
            )}
            {incident.risk_level && (
              <span 
                className="px-1.5 py-0.5 rounded text-white uppercase font-bold text-[10px]"
                style={{ backgroundColor: incident.risk_level.includes('High') ? '#ef4444' : incident.risk_level.includes('Moderate') ? '#f97316' : '#eab308' }}
              >
                {incident.risk_level}
              </span>
            )}
            {incident.timestamp && (
              <span className="text-xs text-slate-500 font-mono">
                {timeAgo(incident.timestamp)}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {incident.description && (
        <p className="text-xs text-slate-600 mb-2">
          {incident.description.substring(0, 200)}{incident.description.length > 200 ? '...' : ''}
        </p>
      )}
      
      {/* Sex offender specific info */}
      {incident.type === 'sex_offender' && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs mb-2">
          <div className="text-red-700 font-semibold">{incident.offense}</div>
          <div className="text-red-600">Compliance: {incident.compliance_status}</div>
        </div>
      )}
      
      {/* Weather specific info */}
      {incident.type === 'weather' && incident.instruction && (
        <div className="bg-cyan-50 border border-cyan-200 rounded p-2 text-xs mb-2">
          <div className="text-cyan-700 font-semibold">Instructions:</div>
          <div className="text-cyan-600">{incident.instruction.substring(0, 150)}...</div>
        </div>
      )}
      
      {/* ICE activity specific info */}
      {incident.type === 'ice_activity' && (
        <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs mb-2">
          <div className="flex justify-between">
            <span className="text-orange-700">Source: {incident.source?.replace('_', ' ')}</span>
            <span className={`font-semibold ${incident.verified ? 'text-green-600' : 'text-orange-600'}`}>
              {incident.verified ? 'Verified' : 'Unverified'}
            </span>
          </div>
          {incident.reports_count > 1 && (
            <div className="text-orange-600">{incident.reports_count} reports</div>
          )}
        </div>
      )}
      
      {/* Camera specific info */}
      {incident.type === 'security_camera' && (
        <div className="bg-slate-100 rounded p-2 text-xs mb-2">
          <div className="text-slate-700">Operator: {incident.operator}</div>
          <div className="flex justify-between">
            <span className="text-slate-600">Type: {incident.camera_type}</span>
            <span className={`font-semibold ${incident.status === 'online' ? 'text-green-600' : 'text-red-600'}`}>
              {incident.status}
            </span>
          </div>
          {incident.has_live_feed && incident.feed_url && !incident.feed_url.includes('Upgrade') && (
            <a href={incident.feed_url} target="_blank" rel="noopener noreferrer" 
               className="text-teal-600 hover:underline mt-1 block">
              View Live Feed
            </a>
          )}
        </div>
      )}
      
      {/* Location info */}
      {(incident.city || incident.state || incident.area_desc) && (
        <div className="bg-slate-100 rounded p-2 text-xs">
          <div className="text-slate-500">Location</div>
          <div className="text-slate-700 font-medium">
            {incident.city ? `${incident.city}, ${incident.state}` : incident.area_desc || incident.state}
          </div>
          {incident.address && <div className="text-slate-500 mt-1">{incident.address}</div>}
        </div>
      )}
      
      {incident.scanner_feed_url && (
        <div className="mt-2 flex items-center gap-2 p-2 bg-teal-50 rounded border border-teal-200">
          <Radio size={14} className="text-teal-600" />
          <span className="text-xs text-teal-700">Live Scanner Available</span>
        </div>
      )}
    </div>
  );
};

// Scanner popup content component with direct play functionality
const ScannerPopupContent = ({ cityData, feeds, onSelectFeed }) => {
  const onlineFeeds = feeds.filter(f => f.status === 'online');
  const totalListeners = feeds.reduce((sum, f) => sum + (f.listeners || 0), 0);
  
  // Get the primary/first online feed for quick play
  const primaryFeed = onlineFeeds[0];
  
  return (
    <div className="p-3 min-w-[300px] max-h-[380px] overflow-hidden" data-testid="scanner-popup">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
          <Headphones className="w-5 h-5 text-teal-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-800">{cityData.city} Scanner</h3>
          <p className="text-xs text-slate-500">{cityData.state} • {cityData.department || 'Public Safety'}</p>
        </div>
        {primaryFeed && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] font-medium">LIVE</span>
          </div>
        )}
      </div>
      
      {/* Quick Play Button - Most prominent action */}
      {primaryFeed && (
        <button
          onClick={() => onSelectFeed(primaryFeed)}
          className="w-full mb-3 flex items-center justify-center gap-2 p-3 rounded-lg bg-teal-500 hover:bg-teal-600 text-white font-medium transition-all shadow-lg shadow-teal-500/30"
          data-testid="quick-play-btn"
        >
          <Play className="w-5 h-5" />
          <span>Listen Now</span>
        </button>
      )}
      
      <div className="flex items-center gap-4 mb-3 text-xs">
        <div className="flex items-center gap-1 text-green-600">
          <Signal className="w-3 h-3" />
          <span>{onlineFeeds.length} online</span>
        </div>
        <div className="flex items-center gap-1 text-slate-500">
          <Users className="w-3 h-3" />
          <span>{totalListeners.toLocaleString()} listening</span>
        </div>
      </div>
      
      {/* Show all feeds if more than one */}
      {feeds.length > 1 && (
        <>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            All Feeds ({feeds.length})
          </div>
          
          <div className="space-y-1 max-h-[140px] overflow-y-auto pr-1">
            {feeds.map((feed) => (
              <button
                key={feed.id}
                onClick={() => onSelectFeed(feed)}
                className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-all ${
                  feed.status === 'online' 
                    ? 'bg-slate-100 hover:bg-teal-50 hover:border-teal-200 border border-transparent' 
                    : 'bg-slate-50 opacity-60 cursor-not-allowed'
                }`}
                disabled={feed.status !== 'online'}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${feed.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
                  <div>
                    <div className="text-xs font-medium text-slate-700">{feed.short_name || feed.name || feed.precinct}</div>
                    <div className="text-[10px] text-slate-500">{feed.type} • {feed.area || feed.county}</div>
                  </div>
                </div>
                {feed.status === 'online' && (
                  <Play className="w-4 h-4 text-teal-500" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
      
      {onlineFeeds.length === 0 && (
        <div className="text-center py-4 text-slate-500">
          <Signal className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <p className="text-xs">No feeds currently online</p>
        </div>
      )}
    </div>
  );
};

// Breadcrumb navigation component
const DrillDownBreadcrumb = ({ path, onNavigate, isDarkTheme }) => {
  const bgClass = isDarkTheme 
    ? 'bg-slate-900/90 border-slate-700' 
    : 'bg-white/90 border-slate-200';
  const textClass = isDarkTheme ? 'text-slate-300' : 'text-slate-600';
  const hoverClass = isDarkTheme 
    ? 'hover:text-white hover:bg-slate-700' 
    : 'hover:text-slate-900 hover:bg-slate-200';
  
  return (
    <div className="flex items-center gap-1 text-sm" data-testid="drill-down-breadcrumb">
      <button 
        onClick={() => onNavigate('usa')}
        className={`px-2 py-1 rounded transition-all duration-200 ${
          path.level === 'usa' 
            ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/30' 
            : `${textClass} ${hoverClass}`
        }`}
      >
        USA
      </button>
      
      {path.state && (
        <>
          <ChevronRight className={`w-4 h-4 ${isDarkTheme ? 'text-slate-500' : 'text-slate-400'}`} />
          <button 
            onClick={() => onNavigate('state', path.state)}
            className={`px-2 py-1 rounded transition-all duration-200 ${
              path.level === 'state' 
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/30' 
                : `${textClass} ${hoverClass}`
            }`}
          >
            {path.state}
          </button>
        </>
      )}
      
      {path.county && (
        <>
          <ChevronRight className={`w-4 h-4 ${isDarkTheme ? 'text-slate-500' : 'text-slate-400'}`} />
          <button 
            onClick={() => onNavigate('county', path.county)}
            className={`px-2 py-1 rounded transition-all duration-200 ${
              path.level === 'county' 
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/30' 
                : `${textClass} ${hoverClass}`
            }`}
          >
            {path.county.replace(' County', '')}
          </button>
        </>
      )}
      
      {path.precinct && (
        <>
          <ChevronRight className={`w-4 h-4 ${isDarkTheme ? 'text-slate-500' : 'text-slate-400'}`} />
          <span className="px-2 py-1 rounded bg-teal-600 text-white shadow-lg shadow-teal-500/30">
            {path.precinct}
          </span>
        </>
      )}
    </div>
  );
};

const MapView = ({ 
  incidents = [], 
  selectedIncident, 
  onSelectIncident, 
  filters = [], 
  drillPath,
  onDrillDown,
  stats = {},
  scannerFeeds = [],
  onSelectScannerFeed,
  focusLocation = null,  // { lat, lng, zoom, name } - when set, map flies to this location
  cameras = [],
  offenders = [],
  weatherAlerts = []
}) => {
  const [mapCenter, setMapCenter] = useState(USA_CENTER);
  const [mapZoom, setMapZoom] = useState(USA_ZOOM);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showScanners, setShowScanners] = useState(true);

  // Handle focus location changes (e.g., when playing a scanner)
  useEffect(() => {
    if (focusLocation && focusLocation.lat && focusLocation.lng) {
      setMapCenter([focusLocation.lat, focusLocation.lng]);
      setMapZoom(focusLocation.zoom || 12);
    }
  }, [focusLocation]);

  // Filter incidents based on active filters
  const filteredIncidents = incidents.filter(incident => {
    if (filters.length === 0) return true;
    return filters.includes(incident.type);
  });

  // Group scanner feeds by city
  const getScannerCityData = useCallback(() => {
    const cityGroups = {};
    scannerFeeds.forEach(feed => {
      const city = feed.city;
      if (!cityGroups[city]) {
        cityGroups[city] = {
          feeds: [],
          city: feed.city,
          state: feed.state,
          department: feed.department,
          lat: feed.location?.lat,
          lng: feed.location?.lng,
          hasOnline: false
        };
      }
      cityGroups[city].feeds.push(feed);
      if (feed.status === 'online') {
        cityGroups[city].hasOnline = true;
      }
    });
    return cityGroups;
  }, [scannerFeeds]);

  // Group incidents by state for clustering at USA level
  const getStateClusterData = useCallback(() => {
    const stateGroups = {};
    filteredIncidents.forEach(incident => {
      const state = incident.state;
      if (!stateGroups[state]) {
        stateGroups[state] = {
          incidents: [],
          lat: incident.location.lat,
          lng: incident.location.lng,
          hasCritical: false
        };
      }
      stateGroups[state].incidents.push(incident);
      if (incident.severity === 'critical') {
        stateGroups[state].hasCritical = true;
      }
    });
    return stateGroups;
  }, [filteredIncidents]);

  // Handle drill down navigation
  useEffect(() => {
    if (!drillPath) return;

    switch (drillPath.level) {
      case 'usa':
        setMapCenter(USA_CENTER);
        setMapZoom(USA_ZOOM);
        break;
      case 'state':
        const stateIncidents = filteredIncidents.filter(i => i.state === drillPath.state);
        if (stateIncidents.length > 0) {
          const avgLat = stateIncidents.reduce((sum, i) => sum + i.location.lat, 0) / stateIncidents.length;
          const avgLng = stateIncidents.reduce((sum, i) => sum + i.location.lng, 0) / stateIncidents.length;
          setMapCenter([avgLat, avgLng]);
          setMapZoom(7);
        }
        break;
      case 'county':
        const countyIncidents = filteredIncidents.filter(i => i.county === drillPath.county);
        if (countyIncidents.length > 0) {
          const avgLat = countyIncidents.reduce((sum, i) => sum + i.location.lat, 0) / countyIncidents.length;
          const avgLng = countyIncidents.reduce((sum, i) => sum + i.location.lng, 0) / countyIncidents.length;
          setMapCenter([avgLat, avgLng]);
          setMapZoom(10);
        }
        break;
      case 'precinct':
        const precinctIncidents = filteredIncidents.filter(i => i.precinct === drillPath.precinct);
        if (precinctIncidents.length > 0) {
          const avgLat = precinctIncidents.reduce((sum, i) => sum + i.location.lat, 0) / precinctIncidents.length;
          const avgLng = precinctIncidents.reduce((sum, i) => sum + i.location.lng, 0) / precinctIncidents.length;
          setMapCenter([avgLat, avgLng]);
          setMapZoom(13);
        }
        break;
      default:
        break;
    }
  }, [drillPath, filteredIncidents]);

  // Fly to selected incident
  useEffect(() => {
    if (selectedIncident) {
      setMapCenter([selectedIncident.location.lat, selectedIncident.location.lng]);
      setMapZoom(14);
    }
  }, [selectedIncident]);

  const handleClusterClick = useCallback((state) => {
    onDrillDown('state', state);
  }, [onDrillDown]);

  // Get current filtered incidents for display
  const getDisplayIncidents = useCallback(() => {
    return filteredIncidents.filter(incident => {
      if (drillPath?.state && incident.state !== drillPath.state) return false;
      if (drillPath?.county && incident.county !== drillPath.county) return false;
      if (drillPath?.precinct && incident.precinct !== drillPath.precinct) return false;
      return true;
    });
  }, [filteredIncidents, drillPath]);

  // Render markers based on drill level
  const renderMarkers = () => {
    // At USA level, show state clusters
    if (drillPath?.level === 'usa') {
      const stateData = getStateClusterData();
      return Object.entries(stateData).map(([state, data]) => (
        <Marker
          key={`cluster-${state}`}
          position={[data.lat, data.lng]}
          icon={createClusterIcon(data.incidents.length, data.hasCritical, isDarkTheme)}
          eventHandlers={{
            click: () => handleClusterClick(state)
          }}
        />
      ));
    }
    
    // At state/county/precinct level, show individual markers
    return getDisplayIncidents().map((incident) => (
      <Marker
        key={incident.id}
        position={[incident.location.lat, incident.location.lng]}
        icon={createIncidentIcon(incident.type, incident.severity, incident.status === 'active')}
        eventHandlers={{
          click: () => onSelectIncident(incident)
        }}
      >
        <Popup>
          <IncidentPopupContent incident={incident} />
        </Popup>
      </Marker>
    ));
  };

  // Render scanner feed markers
  const renderScannerMarkers = () => {
    if (!showScanners || scannerFeeds.length === 0) return null;
    
    const cityData = getScannerCityData();
    
    // Filter based on drill path
    const filteredCities = Object.entries(cityData).filter(([city, data]) => {
      if (drillPath?.state && data.state !== drillPath.state) return false;
      return true;
    });
    
    return filteredCities.map(([city, data]) => {
      if (!data.lat || !data.lng) return null;
      
      return (
        <Marker
          key={`scanner-${city}`}
          position={[data.lat, data.lng]}
          icon={createScannerIcon(data.feeds.length, data.hasOnline, isDarkTheme)}
        >
          <Popup>
            <ScannerPopupContent 
              cityData={data} 
              feeds={data.feeds} 
              onSelectFeed={(feed) => {
                if (onSelectScannerFeed) {
                  onSelectScannerFeed(feed);
                }
              }}
            />
          </Popup>
        </Marker>
      );
    });
  };

  // Create camera icon
  const createCameraIcon = (isDark) => {
    return L.divIcon({
      className: 'custom-camera-marker',
      html: `<div class="flex items-center justify-center w-8 h-8 rounded-full ${
        isDark ? 'bg-blue-600' : 'bg-blue-500'
      } text-white shadow-lg border-2 border-white">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });
  };

  // Render camera markers
  const renderCameraMarkers = () => {
    if (cameras.length === 0) return null;
    
    return cameras.slice(0, 100).map((camera) => {
      // Handle both flat and nested location formats
      const lat = camera.lat || camera.location?.lat;
      const lng = camera.lng || camera.location?.lng;
      if (!lat || !lng) return null;
      
      return (
        <Marker
          key={`camera-${camera.id}`}
          position={[lat, lng]}
          icon={createCameraIcon(isDarkTheme)}
        >
          <Popup>
            <div className="p-2 min-w-[200px]">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-sm">{camera.name}</span>
              </div>
              <p className="text-xs text-slate-500">{camera.city}, {camera.state}</p>
              {camera.address && (
                <p className="text-xs text-slate-400 mt-1">{camera.address}</p>
              )}
              {camera.feed_url && (
                <a 
                  href={camera.feed_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:underline mt-2 block"
                >
                  View Live Feed
                </a>
              )}
            </div>
          </Popup>
        </Marker>
      );
    });
  };

  // Create offender icon
  const createOffenderIcon = (riskLevel, isDark) => {
    const colors = {
      high: 'bg-red-600',
      medium: 'bg-orange-500',
      low: 'bg-yellow-500'
    };
    const color = colors[riskLevel?.toLowerCase()] || colors.medium;
    
    return L.divIcon({
      className: 'custom-offender-marker',
      html: `<div class="flex items-center justify-center w-7 h-7 rounded-full ${color} text-white shadow-lg border-2 border-white">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14]
    });
  };

  // Render offender markers
  const renderOffenderMarkers = () => {
    if (offenders.length === 0) return null;
    
    return offenders.slice(0, 200).map((offender) => {
      // Handle both flat and nested location formats
      const lat = offender.lat || offender.location?.lat;
      const lng = offender.lng || offender.location?.lng;
      if (!lat || !lng) return null;
      
      return (
        <Marker
          key={`offender-${offender.id}`}
          position={[lat, lng]}
          icon={createOffenderIcon(offender.risk_level, isDarkTheme)}
        >
          <Popup>
            <div className="p-2 min-w-[220px]">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-5 h-5 text-red-500" />
                <span className="font-medium text-sm">{offender.name}</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs text-white ${
                  offender.risk_level?.toLowerCase().includes('high') || offender.risk_level?.includes('3') ? 'bg-red-600' :
                  offender.risk_level?.toLowerCase().includes('medium') || offender.risk_level?.includes('2') ? 'bg-orange-500' : 'bg-yellow-500'
                }`}>
                  {offender.risk_level?.toUpperCase() || 'UNKNOWN'} RISK
                </span>
              </div>
              <p className="text-xs text-slate-500">{offender.address}</p>
              <p className="text-xs text-slate-400">{offender.city}, {offender.state}</p>
              {offender.offense && (
                <p className="text-xs text-red-400 mt-1">{offender.offense}</p>
              )}
            </div>
          </Popup>
        </Marker>
      );
    });
  };

  // Create weather icon
  const createWeatherIcon = (severity, isDark) => {
    const colors = {
      extreme: 'bg-red-600',
      severe: 'bg-orange-500',
      moderate: 'bg-yellow-500',
      minor: 'bg-blue-400'
    };
    const color = colors[severity?.toLowerCase()] || colors.moderate;
    
    return L.divIcon({
      className: 'custom-weather-marker',
      html: `<div class="flex items-center justify-center w-8 h-8 rounded-full ${color} text-white shadow-lg border-2 border-white animate-pulse">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });
  };

  // Render weather alert markers
  const renderWeatherMarkers = () => {
    if (weatherAlerts.length === 0) return null;
    
    return weatherAlerts.slice(0, 50).map((alert) => {
      // Handle both flat and nested location formats
      const lat = alert.lat || alert.location?.lat;
      const lng = alert.lng || alert.location?.lng;
      if (!lat || !lng) return null;
      
      return (
        <Marker
          key={`weather-${alert.id}`}
          position={[lat, lng]}
          icon={createWeatherIcon(alert.severity, isDarkTheme)}
        >
          <Popup>
            <div className="p-2 min-w-[240px]">
              <div className="flex items-center gap-2 mb-2">
                <CloudLightning className="w-5 h-5 text-amber-500" />
                <span className="font-medium text-sm">{alert.event_type}</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs text-white ${
                  alert.severity === 'extreme' || alert.severity === 'critical' ? 'bg-red-600' :
                  alert.severity === 'severe' || alert.severity === 'major' ? 'bg-orange-500' :
                  alert.severity === 'moderate' ? 'bg-yellow-500' : 'bg-blue-400'
                }`}>
                  {alert.severity?.toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-slate-500">{alert.area || alert.affected_areas || alert.title}</p>
              {alert.headline || alert.description ? (
                <p className="text-xs text-slate-600 mt-1 line-clamp-3">{alert.headline || alert.description}</p>
              ) : null}
              {alert.expires && (
                <p className="text-xs text-slate-400 mt-1">Expires: {new Date(alert.expires).toLocaleString()}</p>
              )}
            </div>
          </Popup>
        </Marker>
      );
    });
  };

  const currentTile = isDarkTheme ? MAP_TILES.dark : MAP_TILES.light;

  return (
    <div className="absolute inset-0" data-testid="map-container">
      <MapContainer
        center={USA_CENTER}
        zoom={USA_ZOOM}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          attribution={currentTile.attribution}
          url={currentTile.url}
          key={isDarkTheme ? 'dark' : 'light'}
        />
        <ZoomControl position="bottomright" />
        <MapController center={mapCenter} zoom={mapZoom} focusLocation={focusLocation} />
        
        {/* Heatmap layer */}
        {showHeatmap && drillPath?.level !== 'usa' && (
          <HeatmapLayer incidents={getDisplayIncidents()} />
        )}
        
        {renderMarkers()}
        {renderScannerMarkers()}
        {renderCameraMarkers()}
        {renderOffenderMarkers()}
        {renderWeatherMarkers()}
      </MapContainer>

      {/* Map controls - positioned below Tank's banner */}
      <div className="absolute top-44 right-4 z-[15] flex flex-col gap-2" data-testid="map-controls">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsDarkTheme(!isDarkTheme)}
          className={`w-10 h-10 rounded-lg shadow-lg transition-all duration-200 ${
            isDarkTheme 
              ? 'bg-slate-800/90 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' 
              : 'bg-white/90 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
          data-testid="theme-toggle"
          title={isDarkTheme ? 'Switch to light map' : 'Switch to dark map'}
        >
          <MapIcon className="w-4 h-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowHeatmap(!showHeatmap)}
          className={`w-10 h-10 rounded-lg shadow-lg transition-all duration-200 ${
            showHeatmap 
              ? 'bg-teal-600 text-white hover:bg-teal-500' 
              : isDarkTheme 
                ? 'bg-slate-800/90 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' 
                : 'bg-white/90 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
          data-testid="heatmap-toggle"
          title={showHeatmap ? 'Hide heatmap' : 'Show heatmap'}
        >
          <Layers className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowScanners(!showScanners)}
          className={`w-10 h-10 rounded-lg shadow-lg transition-all duration-200 ${
            showScanners 
              ? 'bg-teal-600 text-white hover:bg-teal-500' 
              : isDarkTheme 
                ? 'bg-slate-800/90 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' 
                : 'bg-white/90 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
          data-testid="scanner-toggle"
          title={showScanners ? 'Hide scanner feeds' : 'Show scanner feeds'}
        >
          <Headphones className="w-4 h-4" />
        </Button>
      </div>

      {/* Zoom hint - informational only, not clickable */}
      {drillPath?.level === 'usa' && (
        <div 
          className={`absolute bottom-24 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 rounded-full shadow-lg backdrop-blur-md flex items-center gap-2 transition-all duration-200 pointer-events-none select-none ${
            isDarkTheme 
              ? 'bg-slate-800/90 border border-slate-700' 
              : 'bg-white/90 border border-slate-200'
          }`}
        >
          <ZoomIn className={`w-4 h-4 ${isDarkTheme ? 'text-teal-400' : 'text-teal-600'} animate-pulse`} />
          <span className={`text-xs ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`}>
            Click numbered markers on map to zoom in
          </span>
        </div>
      )}
    </div>
  );
};

export default MapView;
